/**
 * Renders one scene (a stored page) at any scene-local time, offscreen.
 *
 * One retained fabric.StaticCanvas per scene, loaded once; each `renderAt`
 * writes the resolved animation frame onto every object and calls the
 * SYNCHRONOUS `renderAll()` (never requestRenderAll: export needs the pixels
 * now, deterministically).
 *
 * DELIBERATE ASYMMETRY WITH PREVIEW: this canvas has NO restore requirement.
 * We own it, nothing reads its JSON, and `dispose()` throws it away. Only the
 * live-canvas preview path (previewPlayer.ts) carries the baseline-restore
 * contract. The baseline here exists only so frames are resolved from the
 * authored state rather than from the previous frame.
 *
 * The canvas is sized to the artboard's footprint in the output and zoomed,
 * so vector content rasterizes at output resolution instead of being
 * upscaled from the artboard.
 *
 * Video layers: each gets a frame source that is SEEKED (never played) to
 * the exact source time before the synchronous render. Fabric draws whatever
 * is in `_element`, so pointing it at the seeked <video> needs no other
 * drawing code. A clip whose media is missing renders its poster frozen and
 * reports a warning; the export never fails over it.
 */

import type { fabric } from 'fabric';
import type { LayerFrameState, ProjectPage, VideoScene } from '@canvas/shared-types';
import { createSceneStaticCanvas } from '@/lib/offscreenCanvas';
import { resolveFrameState } from './animationPresets';
import { fitModeFor, fitRect, fitScale, type FitMode, type Rect, type Size } from './frameGeometry';
import { abortError, isAbortError } from './exportErrors';
import { videoObjectsOn, type CraftaraVideoObject } from './fabricVideoObject';
import { clipSourceMsAt } from './videoClip';
import { createVideoFrameSource, type VideoFrameSource } from './videoFrameSource';
import {
  applyLayerState,
  presetContextFor,
  readFrameState,
  readLayerAnimation,
} from './layerAnimation';

export interface SceneRenderer {
  readonly pageId: string;
  /** Where the rendered canvas goes in the output frame. */
  readonly fit: Rect;
  readonly fitMode: FitMode;
  /** Letterbox color for `contain`. */
  readonly bgColor: string;
  /** User-facing notes (e.g. a clip rendered as its poster because its media is missing). */
  readonly warnings: readonly string[];
  /** Renders scene-local `localMs` and returns the rendered canvas element. */
  renderAt(localMs: number): Promise<HTMLCanvasElement>;
  dispose(): void;
}

export const MISSING_CLIP_COPY =
  'A video clip’s media couldn’t be loaded, so it shows its still frame instead. Re-import the clip to fix it.';

interface ClipSource {
  obj: CraftaraVideoObject;
  source: VideoFrameSource;
}

/** Open a frame source per clip layer; a clip that can't load keeps its poster. */
async function openClipSources(
  canvas: fabric.Canvas,
  signal: AbortSignal | undefined,
  warnings: string[]
): Promise<ClipSource[]> {
  const out: ClipSource[] = [];
  try {
    for (const obj of videoObjectsOn(canvas)) {
      try {
        const source = await createVideoFrameSource({ clip: obj.craftaraVideo, signal });
        obj.attachElement(source.element);
        out.push({ obj, source });
      } catch (err) {
        if (isAbortError(err)) throw err;
        console.warn('[sceneCompositor] clip media unavailable; using poster', err);
        if (!warnings.includes(MISSING_CLIP_COPY)) warnings.push(MISSING_CLIP_COPY);
      }
    }
  } catch (err) {
    out.forEach((c) => c.source.dispose());
    throw err;
  }
  return out;
}

export async function createSceneRenderer(args: {
  page: ProjectPage;
  scene: VideoScene;
  out: Size;
  signal?: AbortSignal;
}): Promise<SceneRenderer> {
  const { page, scene, out, signal } = args;
  const data = page.canvasData;
  const artboard: Size = { width: data.width || 1200, height: data.height || 800 };
  const fitMode = fitModeFor(artboard, out);
  const fit = fitRect(artboard, out, fitMode);
  const scale = fitScale(artboard, out, fitMode);

  if (signal?.aborted) throw abortError();
  const canvas = await createSceneStaticCanvas(data);
  if (signal?.aborted) {
    canvas.dispose();
    throw abortError();
  }
  canvas.setDimensions({
    width: Math.max(1, Math.round(fit.width)),
    height: Math.max(1, Math.round(fit.height)),
  });
  canvas.setZoom(scale);

  const animated: Array<{ obj: fabric.Object; base: LayerFrameState }> = [];
  for (const obj of canvas.getObjects()) {
    if (readLayerAnimation(obj)) animated.push({ obj, base: readFrameState(obj) });
  }

  const warnings: string[] = [];
  let clips: ClipSource[];
  try {
    clips = await openClipSources(canvas, signal, warnings);
  } catch (err) {
    canvas.dispose();
    throw err;
  }

  let disposed = false;
  return {
    pageId: page.pageId,
    fit,
    fitMode,
    bgColor: scene.bgColor || '#000000',
    warnings,

    async renderAt(localMs) {
      if (disposed) throw new Error('Scene renderer used after dispose');
      for (const clip of clips) {
        if (!clip.obj._videoEl) continue;
        try {
          await clip.source.seekTo(clipSourceMsAt(clip.obj.craftaraVideo, localMs));
        } catch (err) {
          if (isAbortError(err)) throw err;
          // A decode error mid-export freezes this clip on its poster rather than failing the file.
          clip.obj.detachElement();
          if (!warnings.includes(MISSING_CLIP_COPY)) warnings.push(MISSING_CLIP_COPY);
        }
      }
      for (const { obj, base } of animated) {
        applyLayerState(
          obj,
          resolveFrameState({
            base,
            anim: readLayerAnimation(obj),
            sceneLocalMs: localMs,
            sceneDurationMs: scene.durationMs,
            ctx: presetContextFor(obj, base, artboard),
          })
        );
      }
      canvas.renderAll();
      return canvas.getElement();
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      clips.forEach((c) => c.source.dispose());
      canvas.dispose();
    },
  };
}
